import React, { useState } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, Box } from '@mui/material';
import { validatePassword } from '../../utils/validators';
import { ChangePasswordModalProps } from '../types/type';

const ChangePasswordModal: React.FC<ChangePasswordModalProps> = ({ open, handleClose, handleChangePasswordSubmit }) => {
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [passwordErrors, setPasswordErrors] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  const handlePasswordChange = (field: keyof typeof passwordData, value: string) => {
    setPasswordData(prev => ({ ...prev, [field]: value }));
    let error: string | null = null;
    switch (field) {
      case 'currentPassword':
      case 'newPassword':
        ({ error } = validatePassword(value));
        if (field === 'newPassword' && value !== passwordData.confirmPassword && passwordData.confirmPassword) {
          setPasswordErrors(prev => ({ ...prev, confirmPassword: 'New password and confirm password do not match.' }));
        } else if (field === 'newPassword' && value === passwordData.confirmPassword && passwordData.confirmPassword) {
          setPasswordErrors(prev => ({ ...prev, confirmPassword: '' }));
        }
        break;
      case 'confirmPassword':
        if (value !== passwordData.newPassword) {
          error = 'New password and confirm password do not match.';
        }
        break;
    }
    setPasswordErrors(prev => ({ ...prev, [field]: error }));
  };

  const handleSubmit = () => {
    const validations = {
      currentPassword: validatePassword(passwordData.currentPassword),
      newPassword: validatePassword(passwordData.newPassword),
    };

    const newErrors = {
      currentPassword: validations.currentPassword.error || '',
      newPassword: validations.newPassword.error || '',
      confirmPassword: passwordData.confirmPassword !== passwordData.newPassword ? 'New password and confirm password do not match.' : ''
    };

    setPasswordErrors(newErrors);

    if (Object.values(newErrors).some(error => !!error)) {
      return;
    }

    handleChangePasswordSubmit(passwordData);
  };

  return (
    <Dialog open={open} onClose={handleClose}>
      <DialogTitle>Change Password</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1, width: 400 }}>
          <TextField
            fullWidth
            label="Current Password"
            type="password"
            value={passwordData.currentPassword}
            onChange={(e) => handlePasswordChange('currentPassword', e.target.value)}
            error={!!passwordErrors.currentPassword}
            helperText={passwordErrors.currentPassword}
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
          />
          <TextField
            fullWidth
            label="New Password"
            type="password"
            value={passwordData.newPassword}
            onChange={(e) => handlePasswordChange('newPassword', e.target.value)}
            error={!!passwordErrors.newPassword}
            helperText={passwordErrors.newPassword}
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
          />
          <TextField
            fullWidth
            label="Confirm New Password"
            type="password"
            value={passwordData.confirmPassword}
            onChange={(e) => handlePasswordChange('confirmPassword', e.target.value)}
            error={!!passwordErrors.confirmPassword}
            helperText={passwordErrors.confirmPassword}
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} color="inherit">
          Cancel
        </Button>
        <Button onClick={handleSubmit} variant="contained" color="primary">
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ChangePasswordModal;